import { apiClient } from '@/api/api'
import { fetchSearchV1, type ProductTeaser, type SearchV1Request } from '@/features/search/api/searchV1'
import type { GiftIdeasParams } from '../gift-quiz.data'
import { addToWishlist, createWishlist } from '@/features/wishlist/api/wishlist'

/**
 * Decision 4 — `GET /gift/ideas` (signed in, `Currency` header): the backend builds the query from
 * the person's People row and ranks it by her taste; `source` = `ml` | `fallback` (the server's own
 * search when ML is off or down — `reason` says why). Not camelised (same teasers as `/v1/search`).
 */
export interface GiftIdeasResponse {
  items: ProductTeaser[]
  source: 'ml' | 'fallback' | string
  generated_at?: string | null
  reason?: string | null
  fx_unavailable?: boolean
}

export async function fetchGiftIdeas(params: GiftIdeasParams, signal?: AbortSignal): Promise<GiftIdeasResponse> {
  const { data } = await apiClient.get<GiftIdeasResponse>('/gift/ideas', { params, signal })
  return { ...data, items: data.items ?? [] }
}

/** Gift ideas through the catalogue search — the quiz's own path for an API without `/gift/ideas`. */
export async function fetchGiftQuizIdeas(request: SearchV1Request, signal?: AbortSignal) {
  return fetchSearchV1(request, { signal })
}

/**
 * Save to list: a new list named after the recipient, then every idea into it — the API
 * has no batch add. A taken name gets the date appended once.
 */
export async function saveGiftQuizIdeas(name: string, variantIds: number[]): Promise<number> {
  let list: { id: number }
  try {
    list = (await createWishlist(name)).data as { id: number }
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status !== 409 && status !== 422) throw error
    list = (await createWishlist(`${name} · ${new Date().toLocaleDateString()}`)).data as { id: number }
  }
  await Promise.all(variantIds.map((variantId) => addToWishlist(list.id, variantId)))
  return list.id
}
